import type { FileManagerPermission } from '@/composeables/file-manager-capabilities';
import { type Ref } from 'vue';
import { useFileManagerActionVisibility } from '../../permissions/useFileManagerActionVisibility';
import { useFileManagerPermissions } from '../../permissions/useFileManagerPermissions';
import { isFolderLike, type FileSystemItem } from '../../types';
import { getParentPath } from '../../utils';
import { canDragSource } from '../drag-drop/dragGuards';
import { useDragPreviewRef } from '../drag-drop/useDragPreviewRef';
import {
  classifyContextMenuTarget,
  contextMenuBuilders,
  type MenuActions,
} from '../core/contextMenuBuilders';
import { useFileManagerCopy } from '../core/useFileManagerCopy';
import { openContextMenuIfAny, useFileManagerContextMenu } from '../core/useFileManagerContextMenu';
import { useFileManagerCreateFolder } from '../core/useFileManagerCreateFolder';
import { useFileManagerDelete } from '../core/useFileManagerDelete';
import { useFileManagerDetails } from '../core/useFileManagerDetails';
import { useFileManagerRename } from '../core/useFileManagerRename';
import { useFileManagerDrag } from '../drag-drop/useFileManagerDrag';
import { useFileManagerNavigation } from '../core/useFileManagerNavigation';
import { canPreviewItem, useFileManagerPreview } from '../core/useFileManagerPreview';
import { useFileManagerTools } from '../tools/useFileManagerTools';
import { useFileManagerTransfer } from '../transfer/useFileManagerTransfer';
import { useFileManagerUpload } from '../upload/useFileManagerUpload';
import { useFileExplorerSelection } from './useFileManagerSelection';

export type ClickAction = 'extend-range' | 'toggle' | 'select-only';

// Pure: which selection action a click maps to. No refs, no injected state — exported so it can be
// unit-tested directly, the same way `canDropAt()` (dragGuards.ts) and `computeSelectionRange()`
// (useFileManagerSelection.ts) are.
export const resolveClickAction = (
  event: Pick<MouseEvent, 'shiftKey' | 'ctrlKey' | 'metaKey'>,
): ClickAction => {
  if (event.shiftKey) return 'extend-range';
  if (event.ctrlKey || event.metaKey) return 'toggle';
  return 'select-only';
};

export type DoubleClickAction = 'navigate' | 'preview' | 'none';

/**
 * Pure: what a double-click on a row means. Same rationale as `resolveClickAction` above — the
 * gesture's *meaning* is decided here, in something unit-testable that holds no refs, and
 * `onItemDblClick` is left with nothing but the dispatch.
 *
 * Splitting it out matters more here than for a single click, because this mapping is the one that
 * keeps growing: it started as navigate-or-open-details, is navigate-or-preview now, and the next
 * row kind (or a tier that may open but not preview) lands as a branch in this function rather than
 * as another condition tangled into a handler that also calls composables. 'none' is a first-class
 * outcome for exactly that reason: a file this tier cannot preview, or one whose listing has not
 * resolved its Graph coordinates yet, is a real case rather than an oversight — the single click
 * that necessarily preceded this one has already opened the details panel, so doing nothing further
 * is the right fallback, not a dead end.
 */
export const resolveDoubleClickAction = (
  item: FileSystemItem,
  permissions: FileManagerPermission[],
): DoubleClickAction => {
  if (isFolderLike(item)) return 'navigate';
  return canPreviewItem(item, permissions) ? 'preview' : 'none';
};

export type DragStartResolution =
  | { type: 'whole-selection'; items: FileSystemItem[] }
  | { type: 'single'; item: FileSystemItem };

// Pure: given the grabbed item, the currently-visible items, and a selection predicate, decide
// whether the drag should carry the whole selection or collapse to just the grabbed item. Same
// testability rationale as `resolveClickAction` above.
export const resolveDragStart = (
  item: FileSystemItem,
  items: FileSystemItem[],
  isSelected: (path: string) => boolean,
): DragStartResolution => {
  if (isSelected(item.path)) {
    return {
      type: 'whole-selection',
      items: items.filter((i) => isSelected(i.path)),
    };
  }
  return { type: 'single', item };
};

// Pure: the context-menu target set. Right-click-on-an-already-selected-item preserves the whole
// selection; right-click-on-an-unselected-item collapses to just that item — exactly the same
// selected-vs-not branch `resolveDragStart` already resolves, so this just flattens that shape into
// a single array instead of re-deriving the same `isSelected(item.path) ? ... : ...` logic.
export const resolveContextMenuTarget = (
  item: FileSystemItem,
  items: FileSystemItem[],
  isSelected: (path: string) => boolean,
): FileSystemItem[] => {
  const resolution = resolveDragStart(item, items, isSelected);
  return resolution.type === 'whole-selection'
    ? resolution.items
    : [resolution.item];
};

// Used by DetailsTableView.vue: everything about how a list of file-system items reacts to
// click/context-menu/double-click/drag, independent of how the items are laid out visually. Drag
// handlers are meant to be bound directly on each rendered item (`:draggable`,
// `@dragstart`, `@dragend`) rather than delegated from a container, so `item` is always the
// concrete grabbed item — no DOM lookup required.
export const useFileGridInteractions = (items: Ref<FileSystemItem[]>) => {
  const { draggedItems, dragOverPath } = useFileManagerDrag();
  const { transferringPaths } = useFileManagerTransfer();
  const { previewEl } = useDragPreviewRef();
  const { navigateTo } = useFileManagerNavigation();
  const { isSelected, selectOnly, toggle, extendRangeTo } =
    useFileExplorerSelection();
  const { detailsItem, openDetails } = useFileManagerDetails();
  const { openPreview } = useFileManagerPreview();
  const { permissionsAt } = useFileManagerPermissions();
  const contextMenu = useFileManagerContextMenu();
  const { deletingPaths, canDeleteAt, requestDelete } = useFileManagerDelete();
  const { renamingPaths, canRenameAt, requestRename } = useFileManagerRename();
  const { copyingPaths, canCopyAt, requestCopy } = useFileManagerCopy();
  const createFolderState = useFileManagerCreateFolder();
  const uploadState = useFileManagerUpload();
  const { showCreateFolderAt, showCreateFileAt } = useFileManagerActionVisibility();
  // Built once here, during setup(), because each tool's `run` closes over inject()ed state.
  const { toolsFor } = useFileManagerTools();
  // Per item, not a single computed, because draggability is neither purely a permission fact — a
  // mount row is never a drag source whatever the tier — nor pane-wide: the item's own path decides
  // which mount's capabilities apply. Both halves live in dragGuards' canDragSource, shared with the
  // sidebar tree's own DirectoryItem so the two surfaces cannot drift apart.
  const canDragItem = (item: FileSystemItem) =>
    canDragSource(item, permissionsAt(item.path));

  const onItemClick = (item: FileSystemItem, event: MouseEvent) => {
    const action = resolveClickAction(event);
    if (action === 'extend-range') {
      extendRangeTo(
        item.path,
        items.value.map((i) => i.path),
      );
    } else if (action === 'toggle') {
      toggle(item.path);
    } else {
      selectOnly(item.path);
    }
    // A plain click on a file row opens the details panel outright — the double-click that used to be
    // the only way in still works and lands on this same call. Modifier clicks keep the older, weaker
    // rule (`detailsItem.value &&`): they are building a selection, so the panel follows the clicked
    // row only when it is already open, and never pops open mid-range. Folder-shaped rows are
    // untouched either way; the panel is file-only, and their click still just selects.
    if (item.type === 'file' && (action === 'select-only' || detailsItem.value)) {
      openDetails(item);
    }
  };

  const onItemContextMenu = (item: FileSystemItem, event: MouseEvent) => {
    const target = resolveContextMenuTarget(item, items.value, isSelected);
    if (!isSelected(item.path)) {
      selectOnly(item.path);
    }
    const kind = classifyContextMenuTarget(target);
    // Every gate is asked about the RIGHT-CLICKED row's path, not the folder currently open: at the
    // virtual root the two are different mounts (or, for the root itself, no mount at all), so
    // scoping these to currentPath would hide "Create folder" on a library row the user can write to.
    const targetPath = target[0].path;
    // Asked about the WHOLE target, not target[0]: toolsFor gates per item and requires every one of
    // them to qualify, so a selection spanning mounts cannot half-work. `undefined` when empty, so no
    // group — and therefore no orphan divider — is inserted.
    const tools = toolsFor(target);
    const actions: MenuActions = {
      createFolder: showCreateFolderAt(targetPath)
        ? { request: () => createFolderState.openModal(targetPath) }
        : undefined,
      createFile: showCreateFileAt(targetPath)
        ? { request: () => uploadState.openFilePicker(targetPath) }
        : undefined,
      rename:
        canRenameAt(targetPath) && !renamingPaths.value.has(targetPath)
          ? { request: () => requestRename(target[0]) }
          : undefined,
      // Asked about the PARENT, not the row itself: a copy creates a new item in the folder the
      // target lives in, so that folder's capabilities are what decide it. The in-flight half is not
      // cosmetic — while a copy of this item is running, its result is not in the children cache yet,
      // so a second copy would pre-fill the same " (n)" and collide.
      copy:
        canCopyAt(getParentPath(targetPath)) && !copyingPaths.value.has(targetPath)
          ? { request: () => requestCopy(target[0]) }
          : undefined,
      tools: tools.length ? { items: tools } : undefined,
      delete: target.every((item) => canDeleteAt(item.path))
        ? { request: () => requestDelete(target) }
        : undefined,
    };
    const menuItems = contextMenuBuilders[kind](target, actions);
    const opened = openContextMenuIfAny(
      { x: event.clientX, y: event.clientY },
      menuItems,
      contextMenu,
    );
    if (opened) event.preventDefault();
  };

  const isDragSource = (path: string) =>
    draggedItems.value.some((dragged) => dragged.path === path);

  // No openDetails branch any more: the plain click that always precedes a double-click has already
  // pointed the panel at this file, so re-opening it here would only restate that. The preview is
  // handed the item explicitly rather than relying on that click having landed first.
  const onItemDblClick = (item: FileSystemItem) => {
    const action = resolveDoubleClickAction(item, permissionsAt(item.path));
    if (action === 'navigate') {
      navigateTo(item.path);
    } else if (action === 'preview') {
      openPreview(item);
    }
  };

  // Bound directly on each tile/row's native `dragstart` (not a third-party drag library's own
  // callback): the Drag and Drop spec requires `dataTransfer.setDragImage()` to be called
  // *synchronously* while `dragstart` is still dispatching — called any later, the browser has
  // already committed to its default ghost. Native per-item binding also means `item` is already
  // the concrete grabbed item, no DOM lookup needed to figure out what's being dragged.
  const onItemDragStart = (item: FileSystemItem, event: DragEvent) => {
    // Insurance behind the :draggable binding: a grabbed library root must never populate
    // draggedItems, or every drop zone would start evaluating a drop that cannot exist.
    if (!canDragItem(item)) return;
    const resolution = resolveDragStart(item, items.value, isSelected);
    if (resolution.type === 'whole-selection') {
      draggedItems.value = resolution.items;
      if (resolution.items.length > 1 && previewEl.value) {
        event.dataTransfer?.setDragImage(previewEl.value, 0, 0);
      }
    } else {
      selectOnly(resolution.item.path);
      draggedItems.value = [resolution.item];
    }
    // Both verbs advertised, because which one this drag ends in is not knowable here — it depends
    // on which mount it is dropped into. Each drop zone narrows it to a concrete dropEffect.
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copyMove';
  };

  const onItemDragEnd = () => {
    draggedItems.value = [];
    dragOverPath.value = null;
  };

  return {
    transferringPaths,
    // Re-exported alongside transferringPaths for the same reason: DetailsTableView binds all of them
    // to its `.is-dragging`/`.is-deleting`/`.is-renaming`/`.is-copying` row classes without having to
    // inject useFileManagerDelete()/useFileManagerRename()/useFileManagerCopy() itself.
    deletingPaths,
    renamingPaths,
    copyingPaths,
    isSelected,
    isDragSource,
    canDragItem,
    onItemClick,
    onItemContextMenu,
    onItemDblClick,
    onItemDragStart,
    onItemDragEnd,
  };
};
